import type { UploadCapable } from "./contracts.js";
import { RcaApiError } from "./errors.js";
import type { RcaBody, UploadView } from "./types.js";

/**
 * ファイルを置く（FR-4.3 / AC-5 / AC-6）。
 *
 * **方式は SDK が選ぶ。** 利用側は「このファイルを置く」とだけ書けばよい。
 *
 * - 小さいもの → 単発（1 リクエスト）
 * - 大きいもの → 3 段（begin → PUT → complete）
 *
 * 大きいものを分けるのは、Cloudflare が**リクエスト本文の上限を
 * Worker が動く前に弾く**ため（Free/Pro 100 MB、Business 200 MB、
 * Enterprise 500 MB）。ストリームでは回避できない。
 *
 * 上限はデプロイ先のプランで変わり、SDK からは分からない。
 * そこで**単発が 413 を返したら 3 段でやり直す**（Q4）。
 * 閾値が多少ずれても最終的に成功する。
 */

/** どのプランでも単発が通る大きさ。 */
const DEFAULT_SINGLE_SHOT_LIMIT = 8 * 1024 * 1024;

export interface UploadInput {
  /** ワークスペース相対のパス。`File` なら省略できる。 */
  readonly name?: string;
  readonly contentType?: string;
  /** 分かっていれば渡す。方式の選択と上限の事前判定に使う。 */
  readonly size?: number;
}

export interface UploadOptions {
  /** これを超えたら 3 段にする。既定 8 MB。 */
  readonly maxSingleShotBytes?: number;
  readonly signal?: AbortSignal;
}

/** `File` / `Blob` かどうか（環境によって存在しないので慎重に見る）。 */
function isBlobLike(value: unknown): value is Blob & { name?: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { size?: unknown }).size === "number" &&
    typeof (value as { arrayBuffer?: unknown }).arrayBuffer === "function"
  );
}

/** 中身から名前・型・大きさを読み取る。 */
function describe(
  body: RcaBody,
  input: UploadInput,
): { name: string; contentType: string; size: number | undefined } {
  if (isBlobLike(body)) {
    return {
      name: input.name ?? body.name ?? "upload.bin",
      contentType: input.contentType ?? body.type ?? "application/octet-stream",
      size: input.size ?? body.size,
    };
  }

  const size =
    input.size ??
    (body instanceof Uint8Array
      ? body.byteLength
      : body instanceof ArrayBuffer
        ? body.byteLength
        : typeof body === "string"
          ? new TextEncoder().encode(body).byteLength
          : // ReadableStream は長さが分からない。
            undefined);

  return {
    name: input.name ?? "upload.bin",
    contentType: input.contentType ?? "application/octet-stream",
    size,
  };
}

/**
 * 3 段の流れ。
 *
 * `upload.direct` が true なら**外部（R2 の署名付き URL）へ直接送る**。
 * このとき API を経由しないので、エッジの本文上限に縛られない。
 */
async function uploadInSteps(
  client: UploadCapable,
  body: RcaBody,
  meta: { name: string; contentType: string; size: number | undefined },
  options: UploadOptions,
): Promise<UploadView> {
  const started = await client.uploads.begin({
    name: meta.name,
    contentType: meta.contentType,
    ...(meta.size === undefined ? {} : { size: meta.size }),
  });

  if (started.upload.direct) {
    // 外部ストレージへ直接。SDK は本体をバッファしない。
    const response = await fetch(started.upload.url, {
      method: started.upload.method,
      headers: started.upload.headers,
      body: body as never,
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
    if (!response.ok) {
      throw new RcaApiError({
        type: "about:blank",
        title: "Direct upload failed",
        status: response.status,
        code: "upload_failed",
        detail: `直接アップロードに失敗しました (${response.status})`,
      });
    }
  } else {
    await client.uploads.putContent(
      started.uploadId,
      body,
      meta.size === undefined ? {} : { contentLength: meta.size },
    );
  }

  return client.uploads.complete(started.uploadId);
}

/**
 * ファイルを置いて `uploadId` を返す。
 *
 * 返った id を Run の `workspace.uploads` に渡す。
 */
export async function uploadFile(
  client: UploadCapable,
  body: RcaBody,
  input: UploadInput = {},
  options: UploadOptions = {},
): Promise<UploadView> {
  const meta = describe(body, input);
  const limit = options.maxSingleShotBytes ?? DEFAULT_SINGLE_SHOT_LIMIT;

  // 大きいと分かっているなら、最初から 3 段にする。
  if (meta.size !== undefined && meta.size > limit) {
    return uploadInSteps(client, body, meta, options);
  }

  try {
    return await client.uploads.single(meta.name, body, {
      contentType: meta.contentType,
      ...(meta.size === undefined ? {} : { contentLength: meta.size }),
    });
  } catch (error) {
    // ★ 上限はデプロイ先で変わる。弾かれたら正しい方式でやり直す。
    if (error instanceof RcaApiError && error.status === 413) {
      return uploadInSteps(client, body, meta, options);
    }
    throw error;
  }
}
